/**
 * Two thumbs — "ça me va" / "pas celle-là" — for one planned meal.
 *
 * Two variants because the live cards have incompatible space:
 *   'glass'   — desktop SlotCard, floating on the 88px photo, opposite the
 *               replace chip. The meta row was measured at 167px of content
 *               against 141px available, so it cannot host these.
 *   'outline' — mobile MealRow and the 800-1180px DesktopMealCard action row.
 *
 * A down thumb is an opinion, never a veto: it blocks nothing and opens
 * nothing. Proposing an alternative is a separate, already-visible gesture.
 */
import React, { useCallback } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../styles/theme';
import { setMealApproval, selectMealById, getTodayStart } from '../../redux/mealPlanSlice';
import type { RootState } from '../../redux/store';
import { useHouseholdMembers } from '../../hooks/useHouseholdMembers';
import { getApprovalState, getMyVote, getPartnerVote } from './approvalState';

interface Props {
  mealId: string | undefined;
  variant: 'glass' | 'outline';
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export function ApprovalThumbs({ mealId, variant }: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { myUid, myInitial, partnerUid, partnerInitial, householdId } = useHouseholdMembers();
  const meal = useSelector((s: RootState) => (mealId ? selectMealById(s, mealId) : null));

  const input = { meal, myUid, partnerUid, householdId, today: getTodayStart() };
  const state = getApprovalState(input);
  const mine = getMyVote(input);
  const theirs = getPartnerVote(input);

  const cast = useCallback(
    (value: 'yes' | 'no') => (e: any) => {
      e?.stopPropagation?.();
      if (!mealId || !myUid) return;
      // Re-tapping the active thumb retracts the opinion.
      dispatch(setMealApproval({ mealId, uid: myUid, value: mine === value ? null : value }));
    },
    [dispatch, mealId, myUid, mine]
  );

  if (state === 'hidden') return null;

  const glass = variant === 'glass';

  // Agreement replaces the two buttons with one named pill. The thumbs have
  // done their job, and naming the state beats the silence of two neutral
  // buttons — silence reads as "nobody answered". Tapping retracts my yes.
  if (state === 'agreed') {
    return (
      <Pressable
        onPress={cast('yes')}
        hitSlop={HIT}
        accessibilityRole="button"
        accessibilityLabel={t('household.approval.agreedBoth')}
        style={[
          styles.row,
          glass ? styles.rowGlass : styles.rowOutline,
          styles.agreedPill,
          glass && styles.agreedPillGlass,
        ]}
      >
        <Feather name="check" size={glass ? 13 : 14} color={Paprika.approvalYes} />
        {glass ? null : <Text style={styles.agreedText}>{t('household.approval.agreedBoth')}</Text>}
      </Pressable>
    );
  }

  const btn = (kind: 'yes' | 'no', active: boolean, myBadge: boolean, partnerBadge: boolean) => (
    <Pressable
      onPress={cast(kind)}
      hitSlop={HIT}
      accessibilityRole="button"
      accessibilityLabel={t(
        kind === 'yes' ? 'household.approval.agree' : 'household.approval.refuse'
      )}
      style={[
        styles.btn,
        glass ? styles.btnGlass : styles.btnOutline,
        active && (kind === 'yes' ? styles.btnYesOn : styles.btnNoOn),
      ]}
    >
      <Feather
        name={kind === 'yes' ? 'thumbs-up' : 'thumbs-down'}
        size={glass ? 13 : 15}
        color={
          active
            ? kind === 'yes' ? Paprika.approvalYes : Paprika.approvalNo
            : Paprika.textMuted
        }
      />
      {myBadge && myInitial ? (
        <View
          style={[
            styles.badge,
            styles.myBadge,
            { backgroundColor: Paprika.primary },
          ]}
        >
          <Text style={styles.badgeText}>{myInitial}</Text>
        </View>
      ) : null}
      {partnerBadge && partnerInitial ? (
        <View
          style={[
            styles.badge,
            styles.partnerBadge,
            { backgroundColor: kind === 'yes' ? Paprika.approvalYes : Paprika.approvalNo },
          ]}
        >
          <Text style={styles.badgeText}>{partnerInitial}</Text>
        </View>
      ) : null}
    </Pressable>
  );

  return (
    <View style={[styles.row, glass ? styles.rowGlass : styles.rowOutline]}>
      {btn('yes', mine === 'yes', mine === 'yes', theirs === 'yes')}
      {btn('no', mine === 'no', mine === 'no', theirs === 'no')}
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
  rowGlass: { position: 'absolute', top: 6, left: 6, gap: 4 },
  rowOutline: { marginLeft: 'auto' as any, gap: 6 },
  btn: { alignItems: 'center', justifyContent: 'center' },
  btnGlass: {
    width: 26, height: 26, borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.94)',
    boxShadow: '0 2px 8px rgba(82, 68, 60, 0.12)',
  } as any,
  btnOutline: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1, borderColor: Paprika.border,
  },
  btnYesOn: { backgroundColor: Paprika.approvalYesTint, borderColor: Paprika.approvalYesTint },
  btnNoOn: { backgroundColor: Paprika.approvalNoTint, borderColor: Paprika.approvalNoTint },
  badge: {
    position: 'absolute',
    width: 11, height: 11, borderRadius: 6,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1.5, borderColor: Paprika.surfaceLowest,
  },
  myBadge: {
    // My badge positioned top-right; partner's at bottom-right
    top: -1, right: -1,
  },
  partnerBadge: {
    // Partner badge positioned bottom-right, offset so it doesn't overlap with my badge
    bottom: -1, right: -1,
  },
  badgeText: { fontFamily: 'Inter-SemiBold', fontSize: 7, color: Paprika.surfaceLowest },
  agreedPill: {
    gap: 4, paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: 999, backgroundColor: Paprika.approvalYesTint,
  },
  // No room for the label on an 88px photo — the check carries it there.
  agreedPillGlass: { paddingHorizontal: 6, paddingVertical: 6 },
  agreedText: { fontFamily: 'Inter-SemiBold', fontSize: 10.5, color: Paprika.approvalYes },
});
